import qs from "query-string";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import type { ICollectionHandleQuery } from "@repo/shopifyV2/interface/collection";
import type { IProductMetafields } from "@repo/shopifyV2/interface/product";
import type { Product, ProductEdge, ProductVariantConnection } from "@repo/shopifyV2/types/storefront.types";

import { COLLECTION_SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import useCollection from "@queries/useCollection";

import { useLayoutStore } from "@store/layout";

const useCollections = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const [searchParams] = useSearchParams();

  // 스토어 전체를 구독하면 토스트/메뉴 같은 무관한 변경에도 리렌더 되므로 셀렉터로 좁힌다
  const setMeta = useLayoutStore((state) => state.setMeta);

  const { lang } = useLanguage();

  const [collectionData, setCollectionData] = useState<ICollectionHandleQuery>();
  const [products, setProducts] = useState<ProductEdge[]>([]);
  const [expandProducts, setExpandProducts] = useState<ProductEdge[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handle = pathname.includes("collections") && pathname.split("/").pop();

  const queryString = qs.parse(search);

  const prevFilter = useRef<typeof variables.filters>([]);
  const handleRef = useRef<string>("");
  const productLength = useRef(0); // 이전 products의 길이 저장

  const hasNextPage = useMemo(
    () => collectionData?.collection.products.pageInfo.hasNextPage || false,
    [collectionData?.collection.products.pageInfo],
  );

  const endCursor = useMemo(() => collectionData?.collection.products.pageInfo.endCursor, [collectionData?.collection.products.pageInfo]);

  const variables = useMemo(() => {
    // variables이 바뀌면 재조회 하기 때문에 값 초기화
    setExpandProducts([]);
    productLength.current = 0;

    // category / color / size
    const sortQueries = COLLECTION_SORT.find((item) => item.name === queryString.sort);
    const filterQueries = Object.entries(queryString)
      .filter(([key]) => key === "category" || key === "color" || key === "size" || key === "tag")
      .map(([key, value]) => {
        const parseValue = JSON.parse((value as string) || "[]") as string[];
        return parseValue.map((item) => {
          switch (key) {
            case "category":
              return { productType: item };
            case "tag":
              return { tag: item };
            default:
              return { variantOption: { name: key, value: item } };
          }
        });
      })
      .flat();

    return {
      first: searchParams.get("view") && searchParams.get("view") === "seperate" ? 12 : 25, // 쿼리 1회에 조회하는 product 수
      handle,
      sortKey: sortQueries?.value ?? "COLLECTION_DEFAULT",
      reverse: sortQueries?.reverse ?? false,
      filters: filterQueries || [],
    };
  }, [handle, search]);

  const getExpandList = (productList: ProductEdge[]) => {
    const expandList: ProductEdge[] = [];

    productList.forEach(({ node }) => {
      const colorOptions = node.options.find((item) => item.name === "Color")?.values;

      if (colorOptions) {
        colorOptions.forEach((color) => {
          const colorList = node.variants.edges.filter(({ node: variantNode }) =>
            variantNode.selectedOptions.find((option) => option.value === color),
          );

          const newList: ProductEdge = {
            node: {
              ...node,
              options: node.options.map((option) => (option.name === "Color" ? { ...option, values: [color] } : option)),
              variants: { edges: colorList } as ProductVariantConnection,
              availableForSale: colorList.some((item) => item.node.availableForSale),
            },
          } as ProductEdge;

          expandList.push(newList);
        });
      }
    });

    return expandList;
  };

  /** 컬렉션 데이터 쿼리 */
  // 첫 렌더부터 캐시를 동기적으로 읽어 뒤로가기 스크롤 복원을 살린다
  const {
    fetchCollection,
    collectionData: queryData,
    collectionLoading: loading,
    fetchMoreCollection,
    refetchCollection: refetch,
  } = useCollection({ skipCollectionList: true, skipFilters: true, initialCollectionVariables: variables });

  useEffect(() => {
    void fetchCollection(variables);
  }, [variables]);

  useEffect(() => {
    if (!queryData) return;

    if (!queryData.collection) {
      navigate(URLS.HOME);
      return;
    }

    prevFilter.current = variables.filters;

    const edges = queryData.collection.products.edges.filter(
      ({ node }) => (node as Product & IProductMetafields)?.hidden?.value !== "true",
    );

    setCollectionData(queryData);
    setProducts(edges);

    setIsLoading(true);
    const newExpandList = getExpandList(edges.slice(productLength.current));
    setExpandProducts((prev) => [...prev, ...newExpandList]);
    productLength.current = edges.length;
    setIsLoading(false);
  }, [queryData]);

  useEffect(() => {
    setMeta({
      title: `${collectionData?.collection ? `${collectionData?.collection?.title} - ` : ""} ${DEFAULT_TITLE}`,
    });
  }, [collectionData]);

  // useEffect(() => {
  //   showFooter(!hasNextPage);
  // }, [hasNextPage, pathname]);

  const visibleProducts = useMemo(
    () =>
      searchParams.get("view") === "combined"
        ? products
        : expandProducts.filter(({ node }) => (lang !== "jp" && pathname.includes("archive") ? node.availableForSale : true)),
    [searchParams, products, expandProducts, pathname],
  );

  const handleFetchMore = async () => {
    await fetchMoreCollection();
  };

  return {
    collectionData,
    loading: loading || isLoading,
    products: visibleProducts,
    productCount: products.length,
    hasNextPage,
    handleFetchMore,
  };
};

export default useCollections;
